import { bound } from "@frui.ts/helpers";
import { ConductorAllChildrenActive, ScreenBase } from "@frui.ts/screens";
import { getValidationMessage, hasValidation, IHasValidation, validate, validateAll } from "@frui.ts/validation";
import { action, computed, observable, reaction } from "mobx";
import LicensePlateChange from "models/licensePlateChange";
import NiaResponse from "models/niaResponse";
import { PersistedBase64, SignatureType } from "models/notice";
import printJS from "print-js";
import { FileRejection } from "react-dropzone";
import EnumService from "services/enumService";
import EventBus, { NIARedirectFailure } from "services/eventBus";
import ISDSService, { SESSION_STORAGE_ISDS_REQUEST_SOURCE_KEY } from "services/ISDS/isdsService";
import LocalizationService from "services/localizationService";
import NiaService, { Notice, SESSION_STORAGE_NIA_REQUEST_SOURCE_KEY } from "services/NIA/niaService";
import AppRouter from "services/appRouter";
import Order from "models/order";
import Person from "models/person";
import { blobToBase64 } from "utils/base64";
import format from "date-fns-tz/format";
import Omission from "models/omission";
import ExemptionService from "services/exemptionService";
import OmissionService from "services/omissionService";
import LicensePlateChangeService from "services/licensePlateChangeService";
import { scrollToFirstError } from "utils/scrollTo";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import { czechID, MAX_FILE_SIZE_IN_BYTES } from "config";
import { deleteFile, getFile, setFile } from "data/indexedDbConnector";
import { createFileFromBlob } from "utils/fileUtils";
import OutageService from "services/outageService";
import { SystemComponent } from "models/outage";
import OutagesViewModel from "../common/outagesViewModel";
import RefundRequestService from "services/refundRequestService";
import RefundRequestNotice from "models/refundRequestNotice";
import Exemption from "models/exemption";

export type NoticeTypes = Exemption | Omission | LicensePlateChange | RefundRequestNotice;

export default abstract class NoticeViewModel extends ConductorAllChildrenActive<ScreenBase> {
  protected async persistNotice() {
    if (this.notice._noticeBase64) {
      await this.addFile(this.notice._noticeBase64);
    }
  }

  abstract translateGeneral(code: string, disableUnbreakableRules: boolean): string;
  abstract translateAttribute(code: string): string;
  protected abstract createChildren(): void;
  outagesViewModel: OutagesViewModel;
  @observable formElement?: HTMLFormElement;
  @observable showLoadingModal = false;
  @observable errorMessage?: string;
  @observable rejectedPowerOfAttorneyFiles: File[] = [];
  @observable rejectedTechnicalLicenseFiles: File[] = [];

  private errorCodeRegexp = new RegExp(/%code%/g);
  private UNKNOWN_ERROR_CODE = "unknown";

  protected unsubscriptions: Array<() => void> = [];

  @computed
  get notice(): NoticeTypes {
    throw Error("Incomplete implementation");
  }

  @computed
  get allowedFileTypes() {
    return [
      ".pdf",
      ".txt",
      ".cer",
      ".crt",
      ".csv",
      ".der",
      ".doc",
      ".docx",
      ".edi",
      ".fo",
      ".gif",
      ".htm",
      ".html",
      ".isdoc",
      ".isdocx",
      ".jfif",
      ".jpeg",
      ".jpg",
      ".json",
      ".mp2",
      ".mp3",
      ".mpeg",
      ".mpeg1",
      ".mpeg2",
      ".mpg",
      ".odp",
      ".ods",
      ".odt",
      ".p7b",
      ".p7c",
      ".p7f",
      ".p7m",
      ".p7s",
      ".pk7",
      ".png",
      ".ppt",
      ".pptx",
      ".rtf",
      ".tif",
      ".tiff",
      ".tsr",
      ".tst",
      ".wav",
      ".xls",
      ".xlsx",
      ".xml",
      ".zfo",
    ];
  }

  @computed
  get isdsHasActiveOutage() {
    const outage = this.outageService.isdsActiveOutage;

    return outage !== undefined;
  }

  constructor(
    public localizationService: LocalizationService,
    protected enumService: EnumService,
    protected niaService: NiaService,
    protected isdsService: ISDSService,
    protected outageService: OutageService,
    protected eventBus: EventBus,
    protected service: ExemptionService | OmissionService | LicensePlateChangeService | RefundRequestService,
    protected router: AppRouter,
    protected outagesViewModelFactory: ReturnType<typeof OutagesViewModel.Factory>
  ) {
    super();
    this.navigationName = "notice";
    this.eventBus.subscribe(NIARedirectFailure, this.niaOperationFailed);
    this.outagesViewModel = this.outagesViewModelFactory(SystemComponent.ISDS);
    this.attachValidators();
  }

  protected async onInitialize(): Promise<any> {
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean) {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }

    return super.onDeactivate(isClosing);
  }

  protected async onActivate() {
    await this.loadFiles();

    this.unsubscriptions.push(
      reaction(() => this.notice.applicant.mailingAddress, this.persistNotice),
      reaction(() => this.notice.applicant.birthDate, this.persistNotice),
      reaction(() => this.notice.applicant.fullName, this.persistNotice),
      reaction(
        () => this.notice.applicant.phoneNumber,
        number => {
          if (number === null) this.notice.applicant.phoneNumber = undefined;

          this.persistNotice();
        }
      ),
      reaction(
        () => this.notice.applicant.emailAddress,
        emailAddress => {
          if (emailAddress === "") this.notice.applicant.emailAddress = undefined;

          this.persistNotice();
        }
      ),
      reaction(
        () => this.notice._signatureType,
        () => {
          this.createChildren();
          this.persistNotice();
        }
      ),
      reaction(() => this.notice.vehicle?.licensePlate, this.persistNotice),
      reaction(() => this.notice.vehicle?.countryId, this.persistNotice),
      reaction(
        () => this.isNoticeValid,
        isValid => {
          if (isValid && this.errorMessage) {
            this.errorMessage = undefined;
          }
        }
      ),
      reaction(() => this.isdsHasActiveOutage, this.onISDSOutageChanged)
    );

    return super.onActivate();
  }

  @action.bound
  async cancel() {
    this.service.reset();
    this.attachValidators();
    this.createChildren();
    await this.persistNotice();
    this.rejectedPowerOfAttorneyFiles = [];
    this.rejectedTechnicalLicenseFiles = [];
    this.errorMessage = undefined;
  }

  @bound
  showBirthdate() {
    return true;
  }

  @bound
  protected async ISDSSessionIdHandler(data: { type: string; payload: { sessionId: string } }) {
    this.creationLoadingModalToggle(true);
    if (!sessionStorage.getItem(SESSION_STORAGE_ISDS_REQUEST_SOURCE_KEY)) {
      this.creationLoadingModalToggle(false);
      return;
    }

    sessionStorage.removeItem(SESSION_STORAGE_ISDS_REQUEST_SOURCE_KEY);
    if (!this.notice._noticeBase64) {
      this.creationLoadingModalToggle(false);
      return;
    }

    await this.loadFiles();

    await this.uploadDocuments(data.payload.sessionId, [
      this.notice._noticeBase64,
      ...this.notice._powerOfAttorneyFilesBase64,
      ...this.notice._technicalLicenseFilesBase64,
    ]);
  }

  @bound
  protected NiaResponseHandler(data: { type: string; payload: { niaResponse: NiaResponse } }) {
    sessionStorage.removeItem(SESSION_STORAGE_NIA_REQUEST_SOURCE_KEY);
    const { niaResponse } = data.payload;
    if (niaResponse.dateOfBirth) {
      this.notice.applicant.birthDate = new Date(niaResponse.dateOfBirth);
    }

    this.notice.applicant.fullName = `${niaResponse.firstName ? niaResponse.firstName + " " : ""}${
      niaResponse.lastName ? niaResponse.lastName : ""
    }`
      .toLowerCase()
      .split(/ /g)
      .map(word => `${word.substring(0, 1).toUpperCase()}${word.substring(1)}`)
      .join(" ");

    if (niaResponse.address) {
      this.notice.applicant.mailingAddress = this.formatAddress(
        niaResponse.address.streetName,
        niaResponse.address.houseNumber,
        niaResponse.address.postalCode,
        niaResponse.address.municipality
      );
    }

    this.persistNotice();
  }

  private formatAddress = (streetName?: string, houseNumber?: string, postalCode?: string, municipality?: string) =>
    `${streetName ? streetName : ""}${houseNumber ? " " + houseNumber : ""}${postalCode || municipality ? "," : ""}${
      postalCode ? " " + postalCode : ""
    }${municipality ? " " + municipality : ""}`;

  protected attachValidators() {
    attachAutomaticBlurValidator(this.notice.applicant, {
      ...this.notice.applicant.rules,
    });
  }

  filesSizeErrorMessage() {
    let isErrorsVisible = true;
    const sizeOfPowerOfAttorneyFiles = this.notice._powerOfAttorneyFilesBase64.reduce((acc, file) => acc + file.fileSize, 0);
    const sizeOfTechnicalLicenceFiles = this.notice._technicalLicenseFilesBase64.reduce((acc, file) => acc + file.fileSize, 0);
    const totalFilesSize = sizeOfPowerOfAttorneyFiles + sizeOfTechnicalLicenceFiles;

    if (hasValidation(this.notice)) {
      isErrorsVisible = this.notice.__validation.isErrorsVisible;
    }

    if (isErrorsVisible && this.documentsUploadEnabled && totalFilesSize >= MAX_FILE_SIZE_IN_BYTES) {
      return this.localizationService.translateGeneral("documentUploader.maxFilesSizeExceedExceed");
    }

    return undefined;
  }

  @bound
  protected validateNotice() {
    let validVehicleOperator = true;
    if (this.notice.vehicleOperator && this.notice.vehicleOperator._isRepresentedByProxy) {
      validVehicleOperator = validate(
        this.notice.vehicleOperator.isLegalPerson
          ? this.notice.vehicleOperator.legalPerson
          : this.notice.vehicleOperator.naturalPerson
      );
    }

    const validNotice = validateAll([this, this.notice, this.notice.vehicle, this.notice.applicant]);
    let validAttachmentsSizeLimit = true;
    if (this.filesSizeErrorMessage() !== undefined) {
      validAttachmentsSizeLimit = false;
    }

    const validationResult = validNotice && validVehicleOperator && validAttachmentsSizeLimit;

    if (!validationResult) {
      this.errorMessage = this.translateError("invalidData");
      scrollToFirstError(this.formElement);
    }

    return validationResult;
  }

  @computed
  get isNoticeValid() {
    const isNoticeValid = (this.notice as any as IHasValidation<Order>).__validation?.isValid;
    const isVehicleValid = (this.notice.vehicle as any as IHasValidation<Order>).__validation?.isValid;
    const isApplicantValid = (this.notice.applicant as any as IHasValidation<Order>).__validation?.isValid;
    const isAttachmentsValid = this.filesSizeErrorMessage() === undefined;
    let isVehicleOperatorValid = true;
    if (this.notice.vehicleOperator && this.notice.vehicleOperator._isRepresentedByProxy) {
      const operatorPerson = this.notice.vehicleOperator.isLegalPerson
        ? this.notice.vehicleOperator.legalPerson
        : this.notice.vehicleOperator.naturalPerson;
      isVehicleOperatorValid = (operatorPerson as any as IHasValidation<Person>).__validation?.isValid;
    }

    return isNoticeValid && isVehicleValid && isApplicantValid && isAttachmentsValid && isVehicleOperatorValid;
  }

  @action.bound
  protected generalOperationFailed(error: { type: string; payload: { type: string } }) {
    this.creationLoadingModalToggle(false);
  }

  @action.bound
  protected isdsOperationFailed(error: { type: string; payload: { type: string } }) {
    this.creationLoadingModalToggle(false);
    this.errorMessage = this.translateError("isds");
  }

  @action.bound
  protected isdsConceptFailure(error: { type: string; payload: { type: string; code: string | undefined } }) {
    this.creationLoadingModalToggle(false);

    const errorCode = error.payload.code;
    this.errorMessage = this.translateError("isdsConcept").replace(this.errorCodeRegexp, errorCode || this.UNKNOWN_ERROR_CODE);
  }

  @action.bound
  protected isdsUploadOperationSuccess(data: { type: string; payload: { conceptIdent: string } }) {
    this.creationLoadingModalToggle(false);
  }

  @action.bound
  protected isdsConceptStatusCheck(data: { type: string; payload: { success: boolean; notice: Notice } }) {
    return;
  }

  @action.bound
  protected niaOperationFailed(paymentError: { type: string; payload: { type: string } }) {
    this.creationLoadingModalToggle(false);
    this.errorMessage = this.localizationService.translateGeneral("errors.nia");
  }

  @action.bound
  creationLoadingModalToggle(open: boolean) {
    this.showLoadingModal = open;
  }

  @action.bound
  protected onISDSOutageChanged() {
    if (this.isdsHasActiveOutage && this.notice && this.notice._signatureType === SignatureType.DataBox) {
      this.notice._signatureType = SignatureType.ElectronicSignature;
    }
  }

  @computed
  get documentsUploadEnabled() {
    return this.hasToUploadPowerOfAttorney || this.hasToUploadTechnicalLicense;
  }

  @computed
  get hasToUploadPowerOfAttorney() {
    return (
      this.notice._signatureType === SignatureType.DataBox &&
      ((this.notice.vehicleOperator &&
        this.notice.vehicleOperator.naturalPerson &&
        this.notice.vehicleOperator.naturalPerson.isRepresentedByProxy) ||
        (this.notice.vehicleOperator && this.notice.vehicleOperator.isLegalPerson))
    );
  }

  @computed
  get hasToUploadTechnicalLicense() {
    if (this.notice instanceof Omission) {
      return false;
    } else {
      return this.notice._signatureType === SignatureType.DataBox && this.isVehicleRegisteredAbroad;
    }
  }

  @bound
  isErrorsVisible() {
    const notice = this.notice;
    let isErrorsVisible = false;
    if (hasValidation(notice)) {
      isErrorsVisible = notice.__validation.isErrorsVisible;
    }

    return isErrorsVisible;
  }

  @bound
  powerOfAttorneyErrorMessage() {
    return getValidationMessage(this.notice, "_powerOfAttorneyFilesBase64");
  }

  @bound
  technicalLicenseErrorMessage() {
    return getValidationMessage(this.notice, "_technicalLicenseFilesBase64");
  }

  @computed
  get GDPR() {
    return this.translateGeneral("gdpr.description", true);
  }

  @action.bound
  redirectToNia() {
    return;
  }

  @action.bound
  async savePDF() {
    if (!(await this.validateNotice())) {
      return;
    }
    this.creationLoadingModalToggle(true);
    const response = await this.createNotice();
    if (!response) {
      this.creationLoadingModalToggle(false);
      return;
    }
    createFileFromBlob(response);
    this.creationLoadingModalToggle(false);
  }

  @action.bound
  async printPDF() {
    if (!(await this.validateNotice())) {
      return;
    }
    this.creationLoadingModalToggle(true);

    this.createNotice()?.then((response: any) => {
      this.creationLoadingModalToggle(false);
      if (!response) {
        this.creationLoadingModalToggle(false);
        return;
      }
      const url = window.URL.createObjectURL(response.blob);
      if (url) printJS(url);
    });
  }

  @action.bound
  public createNotice(): Promise<string | undefined> {
    return Promise.resolve(undefined);
  }

  @action.bound
  public uploadDocuments(ISDSSessionId: string, files: PersistedBase64[]): Promise<void> | undefined {
    return;
  }

  @action.bound
  async continue() {
    if (!(await this.validateNotice())) {
      return;
    }
    this.creationLoadingModalToggle(true);

    this.createNotice()?.then(async (file: any) => {
      if (!file) {
        this.creationLoadingModalToggle(false);
        return;
      }
      const fetchUrl = window.URL.createObjectURL(file.blob);
      this.notice._noticeURL = fetchUrl;
      this.notice._noticeBlob = await fetch(fetchUrl).then(response => response.blob());
      if (!this.notice._noticeBlob) return;
      this.notice._noticeBase64 = {
        id: Date.now().toString(),
        base64: await blobToBase64(this.notice._noticeBlob),
        fileSize: this.notice._noticeBlob.size,
        fileName: `EDAZ_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`,
      };
      await this.persistNotice();

      this.redirectToISDS();

      this.creationLoadingModalToggle(false);
    });
  }

  @action.bound
  public redirectToISDS(): void {
    return;
  }

  @computed
  get isVehicleRegisteredAbroad() {
    return this.notice.vehicle?.countryId !== czechID;
  }

  @computed
  get canProceedToExternalService() {
    return [SignatureType.DataBox].includes(this.notice._signatureType);
  }

  @computed
  get canPrintNotice() {
    return [SignatureType.OfficiallyVerifiedSignature].includes(this.notice._signatureType);
  }

  @computed
  get canSaveNoticePDF() {
    return [SignatureType.OfficiallyVerifiedSignature, SignatureType.ElectronicSignature].includes(this.notice._signatureType);
  }

  @computed
  get canCancelNotice() {
    return true;
  }

  private processAcceptedFiles(acceptedFiles: File[], type: "powerOfAttorney" | "technicalLicense") {
    acceptedFiles.forEach(async file => {
      const fileBase64 = {
        base64: await blobToBase64(file),
        fileSize: file.size,
        fileName: file.name,
        id: Date.now().toString(),
      };
      const key = type === "powerOfAttorney" ? "_powerOfAttorneyFilesBase64" : "_technicalLicenseFilesBase64";
      this.notice[key].push(fileBase64);
      await this.addFile(fileBase64);
      this.persistNotice();
    });
  }

  private processFileRejections(fileRejections: FileRejection[]) {
    return fileRejections
      ?.filter(fileRejection => !fileRejection.errors.find(error => error.code !== "file-invalid-type"))
      .map(fileRejection => fileRejection.file);
  }

  @action.bound
  public addPowerOfAttorneyFiles(acceptedFiles: File[], fileRejections?: FileRejection[]): void {
    this.processAcceptedFiles(acceptedFiles, "powerOfAttorney");
    if (fileRejections) {
      this.rejectedPowerOfAttorneyFiles = this.processFileRejections(fileRejections);
    }
  }

  @action.bound
  public removePowerOfAttorneyFile = (file: PersistedBase64, fileRejections?: FileRejection[]) => () => {
    this.notice._powerOfAttorneyFilesBase64 = this.notice._powerOfAttorneyFilesBase64.filter(
      powerOfAttorneyFile => powerOfAttorneyFile.id !== file.id
    );

    this.removeFile(file);
    this.persistNotice();
  };

  @action.bound
  public addTechnicalLicenseFiles(acceptedFiles: File[], fileRejections?: FileRejection[]): void {
    this.processAcceptedFiles(acceptedFiles, "technicalLicense");
    if (fileRejections) {
      this.rejectedTechnicalLicenseFiles = this.processFileRejections(fileRejections);
    }
  }

  @action.bound
  public removeTechnicalLicenseFile = (file: PersistedBase64) => () => {
    this.notice._technicalLicenseFilesBase64 = this.notice._technicalLicenseFilesBase64.filter(
      technicalLicenseFile => technicalLicenseFile.id !== file.id
    );

    this.removeFile(file);
    this.persistNotice();
  };

  async addFile(file: PersistedBase64) {
    await setFile(file);
  }

  async removeFile(file: PersistedBase64) {
    await deleteFile(file.id);
  }

  @bound
  protected async loadFiles() {
    if (this.notice._noticeBase64) {
      this.notice._noticeBase64 = await getFile(this.notice._noticeBase64.id);
    }

    const currentPowerOfAttorneyFilesBase64: PersistedBase64[] = [];
    for (const powerOfAttorneyFile of this.notice._powerOfAttorneyFilesBase64) {
      const file = await getFile(powerOfAttorneyFile.id);

      if (file) {
        currentPowerOfAttorneyFilesBase64.push(file);
      }
    }
    this.notice._powerOfAttorneyFilesBase64 = currentPowerOfAttorneyFilesBase64;

    const currentTechnicalLicenseFilesBase64: PersistedBase64[] = [];
    for (const technicalLicenseFile of this.notice._technicalLicenseFilesBase64) {
      const file = await getFile(technicalLicenseFile.id);

      if (file) {
        currentTechnicalLicenseFilesBase64.push(file);
      }
    }
    this.notice._technicalLicenseFilesBase64 = currentTechnicalLicenseFilesBase64;
  }

  setDefaultCountryId() {
    if (this.notice.vehicle && !this.notice.vehicle.countryId) {
      this.notice.vehicle.countryId = czechID;
    }
  }

  translateError(code: string) {
    return this.localizationService.translateGeneral(`errors.${code}`);
  }
}
